import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

const useStorageStore = create(
  persist(
    (set, get) => ({
      storageUsed: 0,
      storageLimit: 52428800, // Default 50MB
      isLoading: false,
      error: null,

      // Set storage data
      setStorageData: (storageUsed, storageLimit) => {
        set({ 
          storageUsed: storageUsed || 0,
          storageLimit: storageLimit || 52428800,
          error: null
        });
      },

      // Fetch storage usage from API
      fetchStorageUsage: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get('/api/storage/usage');
          // Interceptor handles unified structure, response.data is already unwrapped
          console.log('Storage usage response:', response.data);
          
          set({
            storageUsed: response.data.storage_used,
            storageLimit: response.data.storage_limit,
            isLoading: false,
            error: null
          });
          
          return true;
        } catch (error) {
          console.error('Failed to fetch storage usage:', error);
          // Use error handler utility for unified error structure
          const errorMessage = error.apiError?.message || error.response?.data?.error?.message || error.message || 'Failed to fetch storage usage';
          set({
            isLoading: false,
            error: errorMessage
          });
          return false;
        }
      },

      // Update storage usage (called after file operations)
      updateStorageUsage: async () => {
        return await get().fetchStorageUsage();
      },

      // Get storage percentage
      getStoragePercentage: () => {
        const { storageUsed, storageLimit } = get();
        return storageLimit > 0 ? (storageUsed / storageLimit) * 100 : 0;
      },

      // Check if near limit (80%+)
      isNearLimit: () => {
        return get().getStoragePercentage() >= 80;
      },

      // Check if over limit
      isOverLimit: () => {
        return get().storageUsed > get().storageLimit;
      },

      // Format bytes to human readable
      formatBytes: (bytes) => {
        if (!bytes || bytes <= 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
      },

      // Reset storage data
      resetStorage: () => {
        set({
          storageUsed: 0,
          storageLimit: 52428800,
          isLoading: false,
          error: null
        });
      }
    }),
    {
      name: 'storage-store',
      partialize: (state) => ({
        storageUsed: state.storageUsed,
        storageLimit: state.storageLimit
      })
    }
  )
);

export default useStorageStore;
